import { GoogleLogo } from './ui/GoogleLogo';
import { surfaceShadows } from '../surfaceStyles';
import React, { useState } from 'react';
import { Alert, Box, Button, Divider, TextField } from '@mui/material';
import { logger } from '../services/logger';
import { authErrorMessage } from '../services/authErrors';
import { authService } from '../services/authService';
import { usePhoneLayout } from '../hooks/usePhoneLayout';
import { AuthShell } from './ui/AuthShell';

interface LoginProps {
  sessionError?: string | null;
  sessionNotice?: string | null;
}

const Login: React.FC<LoginProps> = ({ sessionError, sessionNotice }) => {
  const phoneLayout = usePhoneLayout();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<'password' | 'google' | null>(null);

  const validateInput = () => {
    if (!email || !password) {
      setError('Please fill in all fields.');
      return false;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return false;
    }
    return true;
  };

  const handlePassword = async () => {
    if (!validateInput()) return;
    setPending('password');
    setError(null);
    try {
      if (isRegistering) await authService.register(email, password);
      else await authService.signIn(email, password);
    } catch (cause) {
      logger.error(
        isRegistering ? 'Registration Error:' : 'Login Error:',
        cause,
      );
      setError(authErrorMessage(cause));
    } finally {
      setPending(null);
    }
  };

  const handleGoogle = async () => {
    setPending('google');
    setError(null);
    try {
      if (phoneLayout) await authService.signInWithGoogleRedirect();
      else await authService.signInWithGooglePopup();
    } catch (cause) {
      logger.error('Google sign-in error:', cause);
      setError(authErrorMessage(cause));
      setPending(null);
    }
  };

  return (
    <AuthShell title={isRegistering ? 'Create Account' : 'Welcome Back'}>
      <Box
        component="form"
        onSubmit={(event) => {
          event.preventDefault();
          void handlePassword();
        }}
        noValidate
        sx={{ width: '100%' }}
      >
        {sessionNotice && !error && !sessionError && (
          <Alert severity="info" aria-live="polite" sx={{ mb: 2 }}>
            {sessionNotice}
          </Alert>
        )}
        {(error || sessionError) && (
          <Alert
            severity="error"
            role="alert"
            aria-live="assertive"
            sx={{ mb: 2 }}
          >
            {error || sessionError}
          </Alert>
        )}
        <Button
          variant="outlined"
          fullWidth
          startIcon={<GoogleLogo />}
          disabled={pending !== null}
          onClick={() => {
            void handleGoogle();
          }}
          sx={{
            mb: 2,
            minHeight: 44,
            '&:active': { boxShadow: surfaceShadows.field },
          }}
        >
          {pending === 'google'
            ? 'Connecting to Google…'
            : 'Continue with Google'}
        </Button>
        <Divider sx={{ mb: 2 }}>or use email</Divider>
        <TextField
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          label="Password"
          type="password"
          name="password"
          autoComplete={isRegistering ? 'new-password' : 'current-password'}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />
        <Button
          variant="contained"
          type="submit"
          fullWidth
          disabled={pending !== null}
          sx={{ mb: 2 }}
        >
          {pending === 'password'
            ? 'Please wait…'
            : isRegistering
              ? 'Create Account'
              : 'Sign In'}
        </Button>
        <Button
          onClick={() => {
            setIsRegistering((value) => !value);
            setError(null);
          }}
          disabled={pending !== null}
          fullWidth
        >
          {isRegistering
            ? 'Already have an account? Sign In'
            : "Don't have an account? Sign Up"}
        </Button>
        <Button
          href="/try"
          disabled={pending !== null}
          fullWidth
          sx={{ mt: 1 }}
        >
          Not ready yet? Try a demo
        </Button>
      </Box>
    </AuthShell>
  );
};

export default Login;
